import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Calendar,
  MapPin,
  DollarSign,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Mail,
  Instagram,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EARLY_BIRD_DEADLINE = new Date("2027-03-01T23:59:59-06:00");
const EARLY_BIRD_FEE = 250;
const REGULAR_FEE = 300;

const getShowcasePricing = () => {
  const now = new Date();
  const isEarlyBird = now < EARLY_BIRD_DEADLINE;
  return {
    isEarlyBird,
    currentFee: isEarlyBird ? EARLY_BIRD_FEE : REGULAR_FEE,
    earlyBirdFee: EARLY_BIRD_FEE,
    regularFee: REGULAR_FEE,
    deadline: EARLY_BIRD_DEADLINE,
    deadlineLabel: "March 1, 2027",
  };
};

const SHOWCASES = {
  mens: {
    key: "mens",
    title: "Men's Showcase",
    dates: "March 19 – 21, 2027",
    location: "Prairie View, TX",
    venue: "Nicks Recreation Center · Prairie View A&M University",
    address: "Prairie View, Texas",
    flyer: "/brand/showcase-mens-flyer.png",
    accent: "text-amber-400",
    accentBg: "bg-amber-400",
    accentBorder: "border-amber-400",
  },
  womens: {
    key: "womens",
    title: "Women's Showcase",
    dates: "April 9 – 11, 2027",
    location: "College Station, TX",
    venue: "Texas A&M Recreation Center",
    address: "College Station, Texas",
    flyer: "/brand/showcase-womens-flyer.png",
    accent: "text-purple-400",
    accentBg: "bg-purple-400",
    accentBorder: "border-purple-400",
  },
};

const POSITIONS = [
  "Point Guard",
  "Shooting Guard",
  "Small Forward",
  "Power Forward",
  "Center",
  "Combo Guard",
  "Wing",
];

const EXPERIENCE = [
  "High School",
  "Junior College",
  "NCAA D1",
  "NCAA D2",
  "NCAA D3",
  "NAIA",
  "Pro / Overseas",
  "Free Agent",
  "Other",
];

const inputCls =
  "w-full bg-transparent border-b border-neutral-700 focus:border-amber-400 text-white py-3 outline-none placeholder:text-neutral-700 transition-colors text-sm";
const labelCls =
  "text-[10px] uppercase tracking-[0.25em] text-neutral-400";

const ShowcaseRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const initialShowcase =
    searchParams.get("showcase") === "womens" ? "womens" : "mens";

  const [form, setForm] = useState({
    showcase: initialShowcase,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    height: "",
    weight: "",
    position: "",
    current_team: "",
    experience_level: "",
    hometown: "",
    highlight_link: "",
    notes: "",
    agree_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const selected = useMemo(() => SHOWCASES[form.showcase], [form.showcase]);
  const pricing = useMemo(() => getShowcasePricing(), []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill out name, email, and phone.");
      return;
    }
    if (!form.agree_to_terms) {
      toast.error("Please agree to the showcase terms to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      const { data } = await axios.post(`${API}/showcase/register`, payload);
      toast.success("You're in. Check your email for next steps.");
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Registration failed.";
      toast.error(typeof detail === "string" ? detail : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Calendar size={14} />
            <span>Southwest Region · 2027</span>
          </div>
          <h1
            data-testid="register-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Secure your
            <br />
            <span className="text-amber-400">spot.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            Register for the AfroAm Southwest Region Men's or Women's Showcase.
            Limited spots. {pricing.isEarlyBird ? (
              <>
                Early Bird fee <span className="text-amber-400 font-bold">${pricing.earlyBirdFee}</span> through{" "}
                <span className="text-white">{pricing.deadlineLabel}</span> — then{" "}
                <span className="text-white">${pricing.regularFee}</span>.
              </>
            ) : (
              <>
                Showcase fee <span className="text-white">${pricing.regularFee}</span>{" "}
                <span className="text-neutral-500">(Early Bird ended {pricing.deadlineLabel})</span>.
              </>
            )}{" "}
            Confirmation and payment instructions will be emailed once you submit.
          </p>
        </div>
      </section>

      {submitted ? (
        <section className="py-16 md:py-24 bg-black border-b border-neutral-900">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative border border-amber-400/40 bg-[#0a0a0a] p-8 md:p-12"
              data-testid="register-success"
            >
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
              <CheckCircle2 size={42} className="text-amber-400 mb-6" />
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.95]">
                Welcome to the
                <br />
                <span className="text-amber-400">movement.</span>
              </h2>
              <p className="text-neutral-300 mt-6 leading-relaxed">
                Hey <span className="text-white">{submitted.first_name}</span> — your
                registration for the{" "}
                <span className="text-white">{submitted.showcase_label}</span> has
                been received. We'll be in touch shortly at{" "}
                <span className="text-white">{submitted.email}</span> with payment
                instructions and your showcase packet.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-900 border border-neutral-900">
                <div className="bg-black p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
                    Registration ID
                  </div>
                  <div className="text-xs text-neutral-300 break-all">
                    {submitted.id}
                  </div>
                </div>
                <div className="bg-black p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
                    Showcase
                  </div>
                  <div className="text-sm text-white">
                    {submitted.showcase_label}
                  </div>
                </div>
                <div className="bg-black p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-2">
                    Fee
                  </div>
                  <div className="font-display text-2xl text-white">
                    ${pricing.currentFee}.00
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                    {pricing.isEarlyBird ? "Early Bird" : "Regular"}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-10">
                <Link
                  to="/"
                  data-testid="register-back-home"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Back to Home
                </Link>
                <a
                  href="mailto:afroambasketball@gmail.com?subject=Showcase%20Payment%20Inquiry"
                  data-testid="register-contact-email"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  <Mail size={14} />
                  Email Coordinator
                </a>
                <a
                  href="https://instagram.com/afroamhoops"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="register-instagram"
                  className="inline-flex items-center gap-2 border border-amber-400/40 text-amber-300 px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-400 hover:text-black transition-colors"
                >
                  <Instagram size={14} />
                  @afroamhoops
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24 bg-black border-b border-neutral-900">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Left: showcase selector + flyer */}
              <div className="lg:col-span-5">
                <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                  — Step 01 · Choose your dates
                </div>

                <div className="space-y-3">
                  {Object.values(SHOWCASES).map((s) => {
                    const active = form.showcase === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setField("showcase", s.key)}
                        data-testid={`select-showcase-${s.key}`}
                        className={`w-full text-left p-5 border transition-all relative ${
                          active
                            ? `${s.accentBorder} bg-[#0a0a0a]`
                            : "border-neutral-800 bg-black hover:border-neutral-600"
                        }`}
                      >
                        <div
                          className={`text-[10px] uppercase tracking-[0.4em] mb-2 ${
                            active ? s.accent : "text-neutral-500"
                          }`}
                        >
                          {active ? "Selected" : "Available"}
                        </div>
                        <div className="font-display uppercase text-2xl md:text-3xl text-white">
                          {s.title}
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-neutral-300">
                            <Calendar size={14} className={s.accent} />
                            {s.dates}
                          </div>
                          <div className="flex items-center gap-2 text-neutral-300">
                            <MapPin size={14} className={s.accent} />
                            {s.location}
                          </div>
                        </div>
                        {s.venue && (
                          <div className="mt-3 pt-3 border-t border-neutral-900 text-[11px] text-neutral-400 leading-relaxed">
                            <div className="text-white">{s.venue}</div>
                            <div className="text-neutral-500">{s.address}</div>
                          </div>
                        )}
                        {active && (
                          <span
                            className={`absolute top-3 right-3 w-2.5 h-2.5 rounded-full ${s.accentBg}`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 border border-neutral-900 bg-[#070707] p-4 space-y-2">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    <div className="flex items-center gap-2">
                      <DollarSign size={14} className={pricing.isEarlyBird ? "text-amber-400" : "text-neutral-500"} />
                      Early Bird
                      <span className="text-[9px] tracking-[0.2em] text-neutral-500 normal-case">
                        (through {pricing.deadlineLabel})
                      </span>
                    </div>
                    <div className={`font-display text-xl ${pricing.isEarlyBird ? "text-amber-400" : "text-neutral-500 line-through"}`}>
                      ${pricing.earlyBirdFee}.00
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    <div className="flex items-center gap-2">
                      <DollarSign size={14} className={!pricing.isEarlyBird ? "text-amber-400" : "text-neutral-500"} />
                      Regular Fee
                    </div>
                    <div className={`font-display text-xl ${!pricing.isEarlyBird ? "text-amber-400" : "text-white"}`}>
                      ${pricing.regularFee}.00
                    </div>
                  </div>
                  <div className="pt-2 border-t border-neutral-900 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white">
                      Your Fee Today
                    </div>
                    <div className="font-display text-2xl text-amber-400">
                      ${pricing.currentFee}.00
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block mt-8">
                  <img
                    src={selected.flyer}
                    alt={`${selected.title} Flyer`}
                    className="w-full h-auto border border-neutral-900"
                    data-testid="showcase-selected-flyer"
                  />
                </div>
              </div>

              {/* Right: form */}
              <div className="lg:col-span-7">
                <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                  — Step 02 · Your details
                </div>

                <form
                  onSubmit={submit}
                  data-testid="register-form"
                  className="space-y-7"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>First Name *</label>
                      <input
                        className={inputCls}
                        value={form.first_name}
                        onChange={(e) => setField("first_name", e.target.value)}
                        data-testid="reg-first-name"
                        placeholder="Jordan"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Last Name *</label>
                      <input
                        className={inputCls}
                        value={form.last_name}
                        onChange={(e) => setField("last_name", e.target.value)}
                        data-testid="reg-last-name"
                        placeholder="Carter"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input
                        type="email"
                        className={inputCls}
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        data-testid="reg-email"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input
                        type="tel"
                        className={inputCls}
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        data-testid="reg-phone"
                        placeholder="(555) 123-4567"
                        required
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Date of Birth</label>
                      <input
                        type="date"
                        className={`${inputCls} [color-scheme:dark]`}
                        value={form.date_of_birth}
                        onChange={(e) => setField("date_of_birth", e.target.value)}
                        data-testid="reg-dob"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Hometown</label>
                      <input
                        className={inputCls}
                        value={form.hometown}
                        onChange={(e) => setField("hometown", e.target.value)}
                        data-testid="reg-hometown"
                        placeholder="Houston, TX"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Height</label>
                      <input
                        className={inputCls}
                        value={form.height}
                        onChange={(e) => setField("height", e.target.value)}
                        data-testid="reg-height"
                        placeholder={`6' 2"`}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Weight</label>
                      <input
                        className={inputCls}
                        value={form.weight}
                        onChange={(e) => setField("weight", e.target.value)}
                        data-testid="reg-weight"
                        placeholder="195 lbs"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Position</label>
                      <select
                        className={`${inputCls} appearance-none [color-scheme:dark]`}
                        value={form.position}
                        onChange={(e) => setField("position", e.target.value)}
                        data-testid="reg-position"
                      >
                        <option value="">Select position</option>
                        {POSITIONS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Experience Level</label>
                      <select
                        className={`${inputCls} appearance-none [color-scheme:dark]`}
                        value={form.experience_level}
                        onChange={(e) =>
                          setField("experience_level", e.target.value)
                        }
                        data-testid="reg-experience"
                      >
                        <option value="">Select level</option>
                        {EXPERIENCE.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Current Team / School</label>
                    <input
                      className={inputCls}
                      value={form.current_team}
                      onChange={(e) => setField("current_team", e.target.value)}
                      data-testid="reg-team"
                      placeholder="e.g., Houston Hawks / Texas Southern"
                    />
                  </div>

                  <div>
                    <label className={labelCls}>Highlight Link (optional)</label>
                    <input
                      className={inputCls}
                      value={form.highlight_link}
                      onChange={(e) =>
                        setField("highlight_link", e.target.value)
                      }
                      data-testid="reg-highlight"
                      placeholder="YouTube / Hudl / Drive link to film"
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Anything we should know? (optional)
                    </label>
                    <textarea
                      rows={3}
                      className={`${inputCls} resize-none`}
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      data-testid="reg-notes"
                      placeholder="Awards, agency representation, travel needs..."
                    />
                  </div>

                  <label
                    className="flex items-start gap-3 text-xs text-neutral-300 leading-relaxed cursor-pointer"
                    data-testid="reg-terms"
                  >
                    <input
                      type="checkbox"
                      checked={form.agree_to_terms}
                      onChange={(e) =>
                        setField("agree_to_terms", e.target.checked)
                      }
                      className="mt-0.5 w-4 h-4 accent-amber-400"
                    />
                    <span>
                      I confirm the information above is accurate. I understand
                      the ${pricing.currentFee} showcase fee ({pricing.isEarlyBird ? `Early Bird — through ${pricing.deadlineLabel}` : "Regular"}) is required to secure my spot and
                      that AfroAm Basketball Association may use my showcase
                      footage for promotional purposes.
                    </span>
                  </label>

                  <div className="pt-4 border-t border-neutral-900 flex flex-wrap items-center gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="reg-submit"
                      className="inline-flex items-center gap-3 bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60"
                    >
                      {loading ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <ArrowRight size={14} />
                      )}
                      Submit Registration
                    </button>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                      Submitting for{" "}
                      <span className={selected.accent}>
                        {selected.title}
                      </span>
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ShowcaseRegisterPage;
